import * as React from 'react'

import moment from 'moment'
import type { Moment } from 'moment'

import { DayActivityModel } from 'app/models/dayActivityModel'
import { formatTimespan } from 'app/common/dateHelper'

import { useScene } from '../context/SceneContext'
import { useConfig } from '../context/ConfigContext'
import { BottomTimeFlag } from './BottomTimeFlag'
import { PRINT_HOUR_DIGIT_PX, PRINT_INK_PX } from '../utils/layout'
import { workPeriodInk } from '../printFacts'
import type { WorkPeriodInk } from '../printFacts'
import { placePrintPeriodLabels } from '../utils/printPeriodLayout'

export type DayActivityBlockProps = {
  dayActivity: DayActivityModel
  index: number
}

const shouldRenderInfo = (time: Moment, startDate: Moment): boolean => {
  const currentTime = moment(time)
  const endDate = startDate.clone().add(1, 'days')
  return currentTime >= startDate && currentTime <= endDate
}

const drawStartDayInfo = (dayActivity: DayActivityModel) => {
  return (
    <div className="tw-text-medium tw-grid tw-w-fit tw-place-items-center tw-rounded tw-bg-[#1F2937]/95 tw-px-1 tw-text-sm tw-text-white">
      {moment(dayActivity.startTime).format('HH:mm')}
    </div>
  )
}

const drawEndDayInfo = (dayActivity: DayActivityModel) => {
  const endTime = moment(dayActivity.endTime)

  return (
    <div className="tw-flex tw-w-fit tw-items-center tw-gap-1 tw-rounded tw-bg-[#1F2937]/90 tw-px-1 tw-text-sm tw-text-white">
      <p className="tw-font-medium tw-text-[#E4E9F7]">
        {endTime.format('HH:mm')}
      </p>
      <div>&Sigma;</div>
      <p className="tw-font-light">{formatTimespan(dayActivity.duration)}</p>
      <div className="tw-h-2 tw-w-2 tw-rounded-full tw-bg-[#E03A3E]"></div>
      <div className="tw-flex tw-items-center tw-text-nowrap tw-font-light">
        {formatTimespan(dayActivity.drivingTime)}
      </div>
    </div>
  )
}

const PrintGutterLine: React.FC<{
  left: number
  boxRef: React.Ref<HTMLDivElement>
  children: React.ReactNode
}> = ({ left, boxRef, children }) => (
  <div
    ref={boxRef}
    style={{
      position: 'absolute',
      left,
      bottom: PRINT_HOUR_DIGIT_PX,
      height: PRINT_INK_PX,
      fontSize: PRINT_INK_PX,
      lineHeight: `${PRINT_INK_PX}px`,
      fontFamily: 'Arial, sans-serif',
      whiteSpace: 'nowrap',
      pointerEvents: 'none',
    }}
  >
    {children}
  </div>
)

const EndClusterInk: React.FC<{
  parts: readonly [string, string, string]
}> = ({ parts }) => {
  const [sigma, driving, endClock] = parts
  return (
    <span>
      <span style={{ color: '#434343' }}>{`Σ ${sigma}`}</span>
      <span style={{ color: '#434343' }}>
        <span
          style={{
            display: 'inline-block',
            width: 6,
            height: 6,
            margin: '0 3px',
            borderRadius: '50%',
            background: '#E03A3E',
            verticalAlign: 'middle',
          }}
        />
        {driving}
      </span>
      <span style={{ color: '#007F0E', marginLeft: 4 }}>{endClock}</span>
    </span>
  )
}

const PrintPeriodInk: React.FC<{
  ink: WorkPeriodInk
  startX: number
  endX: number
  chartWidth: number
}> = ({ ink, startX, endX, chartWidth }) => {
  const startRef = React.useRef<HTMLDivElement>(null)
  const endRef = React.useRef<HTMLDivElement>(null)
  const [placed, setPlaced] = React.useState<ReturnType<
    typeof placePrintPeriodLabels
  > | null>(null)

  React.useLayoutEffect(() => {
    const container = (startRef.current ?? endRef.current)?.parentElement
    setPlaced(
      placePrintPeriodLabels({
        startX: ink.start ? startX : null,
        endX: ink.end ? endX : null,
        startWidth: Math.ceil(
          startRef.current?.getBoundingClientRect().width ?? 0,
        ),
        endWidth: Math.ceil(endRef.current?.getBoundingClientRect().width ?? 0),
        chartWidth: Math.min(chartWidth, container?.clientWidth ?? chartWidth),
      }),
    )
  }, [ink, startX, endX, chartWidth])

  if (!ink.start && !ink.end) return null

  const startLeft = placed ? placed.startLeft : startX
  const endLeft = placed ? placed.endLeft : endX

  return (
    <>
      {ink.start && (
        <PrintGutterLine left={startLeft} boxRef={startRef}>
          <span style={{ color: '#007F0E' }}>{ink.start}</span>
        </PrintGutterLine>
      )}
      {ink.end && (
        <PrintGutterLine left={endLeft} boxRef={endRef}>
          <EndClusterInk parts={ink.end} />
        </PrintGutterLine>
      )}
    </>
  )
}

const readExtensionNo = (dayActivity: DayActivityModel): number | undefined => {
  const rawExtension = (dayActivity as any).dailyDrivingTimeExtensionNo
  return typeof rawExtension === 'number' ? rawExtension : undefined
}

export const DayActivityBlock: React.FC<DayActivityBlockProps> = React.memo(
  (props: DayActivityBlockProps) => {
    const scene = useScene()
    const { printMode } = useConfig()
    const { dayActivity } = props
    const startTime = scene.startTime

    if (printMode) {
      // Inclusive shouldRenderInfo would paint a midnight end on both day images.
      const ink = workPeriodInk({
        chartStart: scene.startTime,
        chartDays: scene.days,
        period: {
          startTime: dayActivity.startTime,
          endTime: dayActivity.endTime,
          duration: dayActivity.duration,
          drivingTime: dayActivity.drivingTime,
          dailyDrivingTimeExtensionNo: readExtensionNo(dayActivity),
        },
      })
      return (
        <PrintPeriodInk
          ink={ink}
          startX={scene.timeToX(dayActivity.startTime)}
          endX={scene.timeToX(dayActivity.endTime)}
          chartWidth={scene.width}
        />
      )
    }

    return (
      <div>
        {shouldRenderInfo(dayActivity.startTime, startTime) && (
          <BottomTimeFlag
            left={scene.timeToX(dayActivity.startTime)}
            chartWidth={scene.width}
            inner="right"
          >
            {drawStartDayInfo(dayActivity)}
          </BottomTimeFlag>
        )}
        {shouldRenderInfo(dayActivity.endTime, startTime) && (
          <BottomTimeFlag
            left={scene.timeToX(dayActivity.endTime)}
            chartWidth={scene.width}
            inner="left"
          >
            {drawEndDayInfo(dayActivity)}
          </BottomTimeFlag>
        )}
      </div>
    )
  },
)

DayActivityBlock.displayName = 'DayActivityBlock'
